// src/components/User/Brackets.jsx
import { useState, useEffect } from 'react';
import api from '../../services/api';

export default function Brackets() {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [event, setEvent] = useState(null);
  const [results, setResults] = useState([]); // This will hold either matches OR placements
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await api.get('/events'); 
        setEvents(res.data);
      } catch (err) {
        console.error("Failed to load events", err);
      }
    };
    fetchEvents();
  }, []);

  // This effect fetches both the event details AND the results (matches or placements)
  useEffect(() => {
    if (!selectedEventId) {
      setEvent(null);
      setResults([]);
      return;
    }

    const fetchEventData = async () => {
      setLoading(true);
      try {
        // First, get the event to check if it's a sport
        const eventRes = await api.get(`/events/${selectedEventId}`);
        const selectedEvent = eventRes.data;
        setEvent(selectedEvent);

        // Then, fetch results based on the event type
        let resultsRes;
        if (selectedEvent.is_sport) {
          // For sports, fetch matches
          resultsRes = await api.get(`/matches/event/${selectedEventId}`);
        } else {
          // For non-sports, fetch placements
          resultsRes = await api.get(`/eventresults/${selectedEventId}`);
        }
        setResults(resultsRes.data);
      } catch (err) {
        console.error("Failed to load results", err);
        setResults([]); // Ensure results is always an array
      } finally {
        setLoading(false);
      }
    };

    fetchEventData();
  }, [selectedEventId]);

  // Render based on the event type
  const renderResults = () => {
    if (loading) {
      return <p className="text-gray-500">Loading results...</p>;
    }

    if (results.length === 0) {
      return <p className="text-gray-500">No results available yet.</p>;
    }

    // If it's a sport, render matches
    if (event?.is_sport) {
      return results.map((match) => (
        <div
          key={match.id}
          className="bg-white p-4 rounded-lg shadow flex flex-col md:flex-row items-center justify-between gap-4"
        >
          <TeamCard
            name={match.team1_name}
            logo={match.team1_logo ? match.team1_logo : '/placeholder.png'}
            score={match.team1_score}
          />
          <span className="text-2xl font-bold text-gray-500">VS</span>
          <TeamCard
            name={match.team2_name}
            logo={match.team2_logo ? match.team2_logo : '/placeholder.png'}
            score={match.team2_score}
          />
          {match.winner_name && (
            <div className="ml-4 text-green-600 font-bold">
              🏆 {match.winner_name}
            </div>
          )}
        </div>
      ));
    }

    // If it's NOT a sport, render placements
    return (
      <div className="mt-4">
        <h3 className="text-lg font-semibold mb-4">Final Placements</h3>
        <div className="space-y-2">
          {results
            .sort((a, b) => (a.placement || 999) - (b.placement || 999)) // Sort by placement
            .map((placement) => {
              const college = placement; // The result object contains college info
              const rankText = college.placement === 1 ? 'Champion' :
                              college.placement === 2 ? '1st Place' :
                              college.placement === 3 ? '2nd Place' :
                              college.placement === 4 ? '3rd Place' : 'Participant';
              return (
                <div key={college.college_id} className="flex items-center p-3 bg-white rounded-lg shadow">
                  <span className={`font-bold mr-3 ${college.placement === 1 ? 'text-yellow-500' : ''}`}>
                    {rankText}
                  </span>
                  <img
                    src={college.logo_url || '/placeholder.png'}
                    alt={college.name}
                    className="w-10 h-10 rounded-full border mr-3"
                  />
                  <span className="font-medium">{college.name}</span>
                </div>
              );
            })}
        </div>
      </div>
    );
  };

  return (
    <section className="mt-12">
      <h2 className="text-2xl font-bold mb-6">Event Brackets & Results</h2>

      <select
        value={selectedEventId}
        onChange={(e) => setSelectedEventId(e.target.value)}
        className="p-3 border rounded mb-6 w-full max-w-md bg-white"
      >
        <option value="">Select an Event</option>
        {events.map(e => (
          <option key={e.id} value={e.id}>
            {e.name} ({e.gender}) • {e.category_name} {e.is_sport ? '• Sport' : '• Non-Sport'}
          </option>
        ))}
      </select>

      {selectedEventId && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold">{event?.name || 'Loading...'}</h3>
          {renderResults()}
        </div>
      )}
    </section>
  );
}

function TeamCard({ name, logo, score }) {
  return (
    <div className="flex items-center gap-3">
      <img
        src={logo || '/placeholder.png'}
        alt={name}
        className="w-10 h-10 rounded-full border"
      />
      <span className="font-medium">{name}</span>
      <span className="ml-2 px-2 py-1 bg-gray-100 rounded text-sm">{score || 0}</span>
    </div>
  );
}